/**
 * Shared question type constants for Reading and Listening forms
 */

export const QuestionTypes = {
  MULTIPLE_CHOICE_SINGLE_ANSWER: "MultipleChoiceSingleAnswer",
  MULTIPLE_CHOICE_MULTIPLE_ANSWER: "MultipleChoiceMultipleAnswer",
  TRUE_FALSE: "TrueFalse",
  YES_NO: "YesNo",
  NOTE_COMPLETION_ONE_WORD: "NoteCompletionOneWord",
  SUMMARY_COMPLETION_TWO_WORDS: "SummaryCompletionTwoWords",
  SENTENCE_COMPLETION_ONE_WORD: "SentenceCompletionOneWord",
  MATCHING_FEATURES: "MatchingFeatures",
  MATCHING_HEADINGS: "MatchingHeadings",
  MATCHING_INFORMATION: "MatchingInformation",
  MATCHING_ENDINGS: "MatchingEndings",
};

// Types that should hide the common question editor
export const hideCommonQuestionTypes = [
  QuestionTypes.TRUE_FALSE,
  QuestionTypes.MULTIPLE_CHOICE_SINGLE_ANSWER,
  QuestionTypes.YES_NO,
  QuestionTypes.MATCHING_FEATURES,
  QuestionTypes.MATCHING_HEADINGS,
  QuestionTypes.MATCHING_INFORMATION,
  QuestionTypes.MATCHING_ENDINGS,
];

// Types that should hide the answer options input
export const hideAnswerOptionsTypes = [
  QuestionTypes.TRUE_FALSE,
  QuestionTypes.MULTIPLE_CHOICE_SINGLE_ANSWER,
  QuestionTypes.YES_NO,
  QuestionTypes.NOTE_COMPLETION_ONE_WORD,
  QuestionTypes.SUMMARY_COMPLETION_TWO_WORDS,
  QuestionTypes.SENTENCE_COMPLETION_ONE_WORD,
];

/**
 * Parse question interval string to count
 * @param {string} interval - e.g., "1-10"
 * @returns {number} - Number of questions
 */
export const parseIntervalToCount = (interval) => {
  if (interval && interval.includes("-")) {
    const parts = interval.split("-");
    if (parts.length >= 2) {
      const lower = parseInt(parts[0].trim(), 10);
      const upper = parseInt(parts[1].trim(), 10);
      if (!isNaN(lower) && !isNaN(upper) && upper >= lower) {
        return upper - lower + 1;
      }
    }
  }
  return 0;
};

/**
 * Extract the final text value from a possibly nested answer object
 */
export const extractAnswerText = (val) => {
  let result = val;
  while (result && typeof result === 'object' && result.answer !== undefined) {
    result = result.answer;
  }
  return result;
};

/**
 * Flatten answer options so that each option has a simple string answer
 */
export const formatAnswerOptions = (options = []) =>
  (options ?? []).map((opt) => ({
    key: opt.key,
    answer: typeof opt.answer === 'object' ? extractAnswerText(opt.answer) : opt.answer,
    ...(opt._id ? { _id: opt._id } : {}),
  }));

/**
 * Convert answer strings into objects with alphabetical keys
 * If existing options are provided, reuse their keys and ids
 */
export const createOptionObjects = (opts, existing = []) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const normalized = formatAnswerOptions(existing ?? []);
  return (opts ?? []).map((opt, index) => {
    const base = normalized[index] || {};
    const rawAnswer = typeof opt === 'string' ? opt : opt.answer ?? opt;
    return {
      key: base.key || alphabet[index] || `Key${index}`,
      answer: extractAnswerText(rawAnswer).trim(),
      ...(base._id ? { _id: base._id } : {}),
    };
  });
};

/**
 * Strip HTML tags from a string
 */
export const stripHtml = (html = '') => html.replace(/<[^>]*>/g, '').trim();
